import { NAV_GROUPS, type NavBadgeCounts, type NavItem, type ScreenId } from '@/config/navigation';

import { SteadoMark } from './SteadoMark';
import { SidebarFooter } from './SidebarFooter';
import { SidebarNavItem } from './SidebarNavItem';

interface SidebarProps {
  readonly activeScreenId: ScreenId | null;
  /**
   * What each badged item is waiting on. A key with no number renders no
   * badge, so the rail never blocks on a count that is slow or has failed.
   */
  readonly badges?: NavBadgeCounts;
  /** Passed by the drawer so following a link also closes it. */
  readonly onNavigate?: () => void;
}

/**
 * The navigation column's contents, without the box around them.
 *
 * Rendered twice: as the fixed rail from `lg` up, and inside a drawer below it.
 * Both need the same links, the same active state and the same footer, and the
 * fastest way to have those drift apart would be to write them twice.
 */
export function SidebarContent({ activeScreenId, badges, onNavigate }: SidebarProps) {
  return (
    <>
      <div className="flex h-14 shrink-0 items-center gap-2.5 px-4">
        <SteadoMark />
        <span className="text-sm font-semibold tracking-tight">Steado</span>
        <span className="ml-auto font-mono text-[11px] text-sidebar-foreground/45">admin</span>
      </div>

      <nav aria-label="Primary" className="flex flex-1 flex-col gap-5 overflow-y-auto px-2.5 pt-1 pb-4">
        {NAV_GROUPS.map((group) => (
          <div key={group.label} className="flex flex-col gap-0.5">
            <h2 className="px-2.5 pb-1.5 text-[11px] leading-none font-semibold tracking-wider text-sidebar-foreground/45 uppercase">
              {group.label}
            </h2>
            {group.items.map((item: NavItem) => {
              const badge = item.badge === undefined ? undefined : badges?.[item.badge];

              return (
                <SidebarNavItem
                  key={item.id}
                  item={item}
                  isActive={item.id === activeScreenId}
                  {...(badge === undefined ? {} : { badge })}
                  {...(onNavigate === undefined ? {} : { onNavigate })}
                />
              );
            })}
          </div>
        ))}
      </nav>

      <SidebarFooter />
    </>
  );
}

/** The fixed rail. Hidden below `lg`, where `AppShell` puts it in a drawer. */
export function Sidebar({ activeScreenId, badges }: SidebarProps) {
  return (
    <div className="hidden w-[232px] shrink-0 flex-col overflow-hidden border-r border-sidebar-border bg-sidebar text-sidebar-foreground lg:flex">
      <SidebarContent activeScreenId={activeScreenId} {...(badges === undefined ? {} : { badges })} />
    </div>
  );
}
